// Google Sign-In functions
// Handles Google OAuth authentication

import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { addDoc, collection, doc, getDoc, getDocs, query, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { getAuth } from '../firebase/config';
import { getDB } from '../firebase/config';
import { mergeOrganizationSettings } from '../constants/organizationSettings';
import { logAuth } from '../utils/audit';
import type { Organization, User } from '../models';

interface GoogleSignInResult {
  success: boolean;
  user?: User;
  organization?: Organization;
  error?: string;
  isNewUser?: boolean;
  needsSetup?: boolean; // Flag indicating user needs to complete organization setup
}

/**
 * Signs in or signs up a user with Google
 * If first user, creates organization automatically
 * If existing user, signs them in
 */
export async function signInWithGoogle(): Promise<GoogleSignInResult> {
  try {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    
    // Request additional scopes if needed
    provider.addScope('profile');
    provider.addScope('email');

    const result = await signInWithPopup(auth, provider);
    const firebaseUser = result.user;
    const isNewUser = result.user.metadata.creationTime === result.user.metadata.lastSignInTime;

    const db = getDB();

    if (isNewUser) {
      // New user - check if first user or needs invitation
      const orgsQuery = query(collection(db, 'organizations'));
      const orgsSnapshot = await getDocs(orgsQuery);
      const isFirstUser = orgsSnapshot.empty;

      let organization: Organization;
      let organizationId: string;

      if (isFirstUser) {
        // First user - create organization
        const orgData: Omit<Organization, 'id'> = {
          name: `${firebaseUser.displayName || 'User'}'s Organization`,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
          settings: mergeOrganizationSettings(),
        };

        const orgRef = await addDoc(collection(db, 'organizations'), orgData);
        organizationId = orgRef.id;
        organization = { id: organizationId, ...orgData };
      } else {
        // New user but not first - they need an invitation
        // For now, we'll create a user without org and they'll need to accept invitation
        // In a real app, you might want to redirect to invitation acceptance
        throw new Error('Please accept an invitation to join an organization');
      }

      // Check if user document already exists (by UID)
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const existingUserDoc = await getDoc(userDocRef);
      
      // Create user document with Firebase Auth UID as document ID
      const userData: Omit<User, 'id'> = {
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName || 'User',
        organizationId,
        role: isFirstUser ? 'admin' : (existingUserDoc.exists() ? existingUserDoc.data().role : 'member'), // First user is always admin
        createdAt: existingUserDoc.exists() ? existingUserDoc.data().createdAt : Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      if (existingUserDoc.exists()) {
        // Update existing document instead of creating duplicate
        await updateDoc(userDocRef, userData);
      } else {
        await setDoc(userDocRef, userData);
      }
      const user: User = { id: firebaseUser.uid, ...userData };

      // Log signup
      await logAuth(firebaseUser.uid, organizationId, 'signup', {
        provider: 'google',
        isFirstUser,
      });

      return {
        success: true,
        user,
        organization,
        isNewUser: true,
      };
    } else {
      // Existing user - check if user document exists
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

      // If user document doesn't exist, or exists but lacks an organizationId,
      // the user needs to complete setup. Without organizationId the backend
      // returns 403 on every data request, so the app appears empty.
      if (!userDoc.exists() || !userDoc.data()?.organizationId) {
        return {
          success: true,
          isNewUser: false,
          needsSetup: true,
        };
      }

      // User document exists - normal login flow
      const userData = { id: userDoc.id, ...userDoc.data() } as User;

      // Load organization
      let organization: Organization | undefined;
      if (userData.organizationId) {
        const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
        if (orgDoc.exists()) {
          organization = { id: orgDoc.id, ...orgDoc.data() } as Organization;
        }
      }

      // Log login
      await logAuth(firebaseUser.uid, userData.organizationId, 'login', {
        provider: 'google',
      });

      return {
        success: true,
        user: userData,
        organization,
        isNewUser: false,
      };
    }
  } catch (error) {
    console.error('Google sign-in error:', error);
    return {
      success: false,
      error: (error as Error).message,
    };
  }
}
